import { useEffect, useRef } from 'react';
import { AppCta } from '../components/AppCta';
import { LocaleSwitcher } from '../components/LocaleSwitcher';
import { RaceAtlasLogo } from '../components/RaceAtlasLogo';
import { useMessages } from '../i18n/LocaleContext';

export const Home = () => {
  const c2oRef = useRef<HTMLDivElement>(null);
  const t = useMessages();

  // Play the chaos→order resolve once, when the widget reaches the centre band
  // of the viewport (not the top edge) — so it lands while you're looking at it.
  useEffect(() => {
    const c2o = c2oRef.current;
    if (!c2o) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      c2o.classList.add('go');
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          c2o.classList.add('go');
          io.disconnect();
        }
      },
      { rootMargin: '-30% 0px -30% 0px' },
    );
    io.observe(c2o);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* header */}
      <header>
        <div className="wrap hbar">
          <RaceAtlasLogo className="logo" />
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <LocaleSwitcher />
            <span className="soon">{t.nav.soon}</span>
            <AppCta />
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="hero">
        <div className="wrap hero-grid">
          <div>
            <span className="eyebrow">{t.hero.eyebrow}</span>
            <h1>
              {t.hero.headlineLine1}
              <br />
              {t.hero.headlineLine2}
            </h1>
            <p className="lead">{t.hero.lead}</p>
            <div className="hero-cta">
              <AppCta />
              <span className="soon">{t.hero.ctaNote}</span>
            </div>
          </div>
          <div>
            <div className="phone">
              <div className="screen">
                <div className="s-top">
                  <RaceAtlasLogo className="logo" />
                </div>
                <div className="ctx">
                  <span>
                    {t.mock.from} <b>Dublin</b>
                  </span>
                  <span className="sep">·</span>
                  <span>
                    <b>May–Aug</b>
                  </span>
                  <span className="sep">·</span>
                  <span>
                    <b>OCR + Hyrox</b>
                  </span>
                </div>
                <div className="sort">
                  <span className="on">{t.mock.sortCost}</span>
                  <span className="off">{t.mock.sortDate}</span>
                </div>
                <div className="row">
                  <div className="r-main">
                    <div className="r-eyebrow">{t.mock.bestValue}</div>
                    <div className="r-top">
                      <span className="r-name">Spartan Sardinia</span>
                      <span className="r-date num">23 Aug</span>
                    </div>
                    <div className="r-bottom">
                      <span>
                        <span className="bead high"></span>
                      </span>
                      <span className="ic">
                        <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.2 4.5 13 8l5.5-1.2c.9-.2 1.6.9 1 1.6L16 12l1.5 5.5c.2.9-.9 1.5-1.5.9L13 15l-3 4.5c-.4.6-1.4.4-1.5-.4L8 14l-4-1.2c-.8-.3-.8-1.4 0-1.7L8 10l.7-5c.1-.8 1.1-1 1.5-.5Z" />
                        </svg>{' '}
                        2h55
                      </span>
                      <span>2 nts</span>
                    </div>
                  </div>
                  <div className="r-price">
                    <div className="p num">~€540</div>
                    <div className="l">{t.mock.allIn}</div>
                  </div>
                </div>
                <div className="row">
                  <div className="r-main">
                    <div className="r-top">
                      <span className="r-name">Hyrox Barcelona</span>
                      <span className="r-date num">5 Jul</span>
                    </div>
                    <div className="r-bottom">
                      <span>
                        <span className="bead med"></span>
                      </span>
                      <span className="ic">
                        <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.2 4.5 13 8l5.5-1.2c.9-.2 1.6.9 1 1.6L16 12l1.5 5.5c.2.9-.9 1.5-1.5.9L13 15l-3 4.5c-.4.6-1.4.4-1.5-.4L8 14l-4-1.2c-.8-.3-.8-1.4 0-1.7L8 10l.7-5c.1-.8 1.1-1 1.5-.5Z" />
                        </svg>{' '}
                        2h15
                      </span>
                      <span>1 nt</span>
                    </div>
                  </div>
                  <div className="r-price">
                    <div className="p num">~€610</div>
                    <div className="l">{t.mock.allIn}</div>
                  </div>
                </div>
                <div className="row">
                  <div className="r-main">
                    <div className="r-top">
                      <span className="r-name">Spartan Andorra</span>
                      <span className="r-date num">14 Jun</span>
                    </div>
                    <div className="r-bottom">
                      <span>
                        <span className="bead high"></span>
                      </span>
                      <span className="ic">
                        <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.2 4.5 13 8l5.5-1.2c.9-.2 1.6.9 1 1.6L16 12l1.5 5.5c.2.9-.9 1.5-1.5.9L13 15l-3 4.5c-.4.6-1.4.4-1.5-.4L8 14l-4-1.2c-.8-.3-.8-1.4 0-1.7L8 10l.7-5c.1-.8 1.1-1 1.5-.5Z" />
                        </svg>{' '}
                        2h40
                      </span>
                      <span>2 nts</span>
                    </div>
                  </div>
                  <div className="r-price">
                    <div className="p num">~€780</div>
                    <div className="l">{t.mock.allIn}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* problem */}
      <section className="sec">
        <div className="wrap grid2">
          <div className="stack-h">
            <span className="eyebrow">{t.problem.eyebrow}</span>
            <h2>{t.problem.heading}</h2>
            <p className="body">{t.problem.body1}</p>
            <p className="body">{t.problem.body2}</p>
          </div>
          <div className="c2o" ref={c2oRef}>
            <div className="scat" style={{ top: '4px', left: '2px', transform: 'rotate(-8deg)' }}>
              <div className="bar">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="u">skyscanner.com</span>
              </div>
              <div className="b">
                <span className="t">DUB → BCN</span>
                <span className="p">€212</span>
              </div>
            </div>
            <div className="scat" style={{ top: '0', right: '0', transform: 'rotate(7deg)' }}>
              <div className="bar">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="u">booking.com</span>
              </div>
              <div className="b">
                <span className="t">Hotel · 2 nts</span>
                <span className="p">€185</span>
              </div>
            </div>
            <div
              className="scat"
              style={{ top: '112px', left: '14px', transform: 'rotate(-4deg)' }}
            >
              <div className="bar">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="u">trips_2026.xlsx</span>
              </div>
              <div className="b">
                <span className="t">{t.problem.realCost}</span>
                <span className="p">≈ ?</span>
              </div>
            </div>
            <div className="scat" style={{ top: '152px', right: '4px', transform: 'rotate(6deg)' }}>
              <div className="bar">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="u">Race Crew</span>
              </div>
              <div className="b" style={{ color: 'var(--muted)', fontSize: '10px' }}>
                <span>{t.problem.crewMessage}</span>
              </div>
            </div>
            <div
              className="scat"
              style={{ top: '254px', left: '46px', transform: 'rotate(-3deg)' }}
            >
              <div className="bar">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="u">ryanair.com</span>
              </div>
              <div className="b">
                <span className="t">{t.problem.returnLeg}</span>
                <span className="p">€89</span>
              </div>
            </div>
            <div className="order">
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'oklch(0.68 0 0)',
                  }}
                >
                  {t.problem.yourWeekend}
                </span>
                <span className="o-sorted">{t.problem.sorted}</span>
              </div>
              <div className="o-rows">
                <div className="o-row">
                  <span>{t.problem.flights}</span>
                  <span className="num">€226</span>
                </div>
                <div className="o-row">
                  <span>{t.problem.stay}</span>
                  <span className="num">€185</span>
                </div>
                <div className="o-row">
                  <span>{t.problem.race}</span>
                  <span className="num">€129</span>
                </div>
              </div>
              <div className="o-total">
                <span>{t.problem.allIn}</span>
                <span className="num">€540</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* discovery hook */}
      <section className="sec" style={{ background: 'var(--surface)' }}>
        <div className="wrap">
          <span className="eyebrow">{t.discovery.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '18ch' }}>{t.discovery.heading}</h2>
          <p className="lead" style={{ marginTop: '18px', maxWidth: '60ch' }}>
            {t.discovery.lead}
          </p>
          <div className="feat3">
            <div className="feat">
              <div className="k">{t.discovery.feat1Kicker}</div>
              <h3>{t.discovery.feat1Heading}</h3>
              <p>{t.discovery.feat1Body}</p>
            </div>
            <div className="feat">
              <div className="k">{t.discovery.feat2Kicker}</div>
              <h3>{t.discovery.feat2Heading}</h3>
              <p>{t.discovery.feat2Body}</p>
            </div>
            <div className="feat">
              <div className="k">{t.discovery.feat3Kicker}</div>
              <h3>{t.discovery.feat3Heading}</h3>
              <p>{t.discovery.feat3Body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* two jobs */}
      <section className="sec">
        <div className="wrap">
          <span className="eyebrow">{t.twoJobs.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '22ch' }}>{t.twoJobs.heading}</h2>
          <div className="jobs">
            <div className="job">
              <div className="tag">{t.twoJobs.freeTag}</div>
              <h3>{t.twoJobs.freeHeading}</h3>
              <p>{t.twoJobs.freeBody}</p>
            </div>
            <div className="job" style={{ borderColor: 'var(--ink)' }}>
              <div className="tag">{t.twoJobs.paidTag}</div>
              <h3>{t.twoJobs.paidHeading}</h3>
              <p>{t.twoJobs.paidBody}</p>
            </div>
          </div>
        </div>
      </section>

      {/* paid payoff */}
      <section className="sec" style={{ background: 'var(--surface)' }}>
        <div className="wrap grid2">
          <div className="stack-h">
            <span className="eyebrow">{t.payoff.eyebrow}</span>
            <h2>{t.payoff.heading}</h2>
            <p className="body">{t.payoff.body1}</p>
            <p className="body">{t.payoff.body2}</p>
          </div>
          <div className="picked-card">
            <div
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: '6px',
              }}
            >
              {t.payoff.cardDay}
            </div>
            <div className="tl">
              <div className="tl-item">
                <span className="tl-node"></span>
                <div className="tl-time num">07:15</div>
                <div className="tl-label">{t.payoff.leaveHotel}</div>
                <div className="tl-sub">{t.payoff.leaveHotelSub}</div>
              </div>
              <div className="tl-item">
                <span className="tl-node"></span>
                <div className="tl-time num">07:20</div>
                <div className="tl-label">{t.payoff.coffee}</div>
                <div className="tl-sub">{t.payoff.coffeeSub}</div>
              </div>
              <div className="tl-item anchor">
                <span className="tl-node"></span>
                <div className="tl-time num">09:30</div>
                <div className="tl-label">{t.payoff.raceStart}</div>
              </div>
              <div className="tl-item">
                <span className="tl-node"></span>
                <div className="tl-time num">~12:30</div>
                <div className="tl-label">{t.payoff.postRace}</div>
                <div className="tl-sub">{t.payoff.postRaceSub}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* who */}
      <section className="sec">
        <div className="wrap">
          <span className="eyebrow">{t.who.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '20ch' }}>{t.who.heading}</h2>
          <p className="lead" style={{ marginTop: '18px', maxWidth: '62ch' }}>
            {t.who.lead}
          </p>
        </div>
      </section>

      {/* pricing */}
      <section className="sec" style={{ background: 'var(--surface)' }}>
        <div className="wrap">
          <span className="eyebrow">{t.pricing.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '22ch' }}>{t.pricing.heading}</h2>
          <div className="prices">
            <div className="price">
              <div className="ptier">
                <span className="t">{t.pricing.freeTier}</span>
              </div>
              <div>
                <div className="amt num">{t.pricing.freeAmount}</div>
                <div className="cad">{t.pricing.freeCadence}</div>
              </div>
              <p className="pdesc">{t.pricing.freeDesc}</p>
              <ul>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.freeItem1}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.freeItem2}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.freeItem3}
                </li>
              </ul>
            </div>
            <div className="price hot">
              <div className="ptier">
                <span className="t">{t.pricing.perTripTier}</span>
                <span className="pill-lime">{t.pricing.perTripBadge}</span>
              </div>
              <div>
                <div className="amt num">{t.pricing.perTripAmount}</div>
                <div className="cad">{t.pricing.perTripCadence}</div>
              </div>
              <p className="pdesc">{t.pricing.perTripDesc}</p>
              <ul>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.perTripItem1}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.perTripItem2}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.perTripItem3}
                </li>
              </ul>
            </div>
            <div className="price">
              <div className="ptier">
                <span className="t">{t.pricing.seasonTier}</span>
                <span className="pill-soon">{t.pricing.seasonBadge}</span>
              </div>
              <div>
                <div className="amt num">{t.pricing.seasonAmount}</div>
                <div className="cad">{t.pricing.seasonCadence}</div>
              </div>
              <p className="pdesc">{t.pricing.seasonDesc}</p>
              <ul>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.seasonItem1}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.seasonItem2}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>{' '}
                  {t.pricing.seasonItem3}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* final */}
      <section className="final">
        <div className="wrap">
          <h2 style={{ maxWidth: '18ch' }}>{t.final.heading}</h2>
          <p className="lead" style={{ marginTop: '16px' }}>
            {t.final.lead}
          </p>
          <div style={{ marginTop: '28px' }}>
            <AppCta />
          </div>
        </div>
      </section>

      {/* footer */}
      <footer>
        <div className="wrap fbar">
          <RaceAtlasLogo className="logo" />
          <div className="flinks">
            <span>{t.footer.privacy}</span>
            <span>{t.footer.terms}</span>
            <a href="mailto:hello@race-atlas.com">{t.footer.contact}</a>
          </div>
        </div>
      </footer>
    </>
  );
};
